import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UpcomingWorkshops = ({ onRegisterWorkshop, onViewAllWorkshops }) => {
  const upcomingWorkshops = [
    {
      id: 1,
      title: "IoT Sensor Integration Workshop",
      instructor: "Dr. Michael Chen",
      instructorTitle: "IoT Systems Engineer",
      date: new Date(2025, 8, 15, 14, 0), // September 15, 2025, 2:00 PM
      duration: "2 hours",
      format: "Virtual",
      capacity: 50,
      registered: 32,
      difficulty: "Intermediate",
      topics: ["Sensor Networks", "Data Collection", "Protocol Design"],
      description: "Learn hands-on techniques for integrating various IoT sensors into smart city infrastructure projects.",
      image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400"
    },
    {
      id: 2,
      title: "GIS Data Visualization Masterclass",
      instructor: "Sarah Williams",
      instructorTitle: "Urban Planning Specialist",
      date: new Date(2025, 8, 18, 10, 0), // September 18, 2025, 10:00 AM
      duration: "3 hours",
      format: "Hybrid",
      capacity: 30,
      registered: 18,
      difficulty: "Advanced",
      topics: ["GIS Mapping", "Data Visualization", "Urban Analytics"],
      description: "Master advanced GIS techniques for creating compelling visualizations of urban data and city planning insights.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400"
    },
    {
      id: 3,
      title: "AI for Smart Transportation",
      instructor: "Prof. James Rodriguez",
      instructorTitle: "AI Research Director",
      date: new Date(2025, 8, 22, 16, 0), // September 22, 2025, 4:00 PM
      duration: "90 minutes",
      format: "Virtual",
      capacity: 75,
      registered: 45,
      difficulty: "Beginner",
      topics: ["Machine Learning", "Traffic Optimization", "Predictive Analytics"],
      description: "Explore how artificial intelligence is revolutionizing urban transportation systems and traffic management.",
      image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400"
    }
  ];

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getFormatIcon = (format) => {
    return format === 'Virtual' ? 'Video' : 'MapPin';
  };

  const getAvailabilityStatus = (registered, capacity) => {
    const percentage = (registered / capacity) * 100;
    if (percentage >= 90) return { status: 'Almost Full', color: 'text-red-600' };
    if (percentage >= 70) return { status: 'Filling Fast', color: 'text-yellow-600' };
    return { status: 'Available', color: 'text-green-600' };
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Upcoming Workshops</h2>
        <Button variant="ghost" size="sm" onClick={onViewAllWorkshops} iconName="Calendar" iconPosition="right">
          View Calendar
        </Button>
      </div>
      <div className="space-y-4">
        {upcomingWorkshops?.map((workshop) => {
          const availability = getAvailabilityStatus(workshop?.registered, workshop?.capacity);
          
          return (
            <div key={workshop?.id} className="border border-border rounded-lg p-4 hover:border-primary/20 transition-all duration-200">
              <div className="flex flex-col lg:flex-row lg:items-start space-y-4 lg:space-y-0 lg:space-x-4">
                <img
                  src={workshop?.image}
                  alt={workshop?.title}
                  className="w-full lg:w-24 h-32 lg:h-16 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2">
                    <h3 className="font-semibold text-foreground mb-1 sm:mb-0">{workshop?.title}</h3>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(workshop?.difficulty)}`}>
                        {workshop?.difficulty}
                      </span>
                      <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                        <Icon name={getFormatIcon(workshop?.format)} size={12} />
                        <span>{workshop?.format}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-2">
                    <div className="flex items-center space-x-1">
                      <Icon name="User" size={14} />
                      <span>{workshop?.instructor}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={14} />
                      <span>{workshop?.duration}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm mb-3">
                    <div className="flex items-center space-x-1 text-foreground">
                      <Icon name="Calendar" size={14} />
                      <span>{formatDate(workshop?.date)}</span>
                    </div>
                    <div className={`flex items-center space-x-1 ${availability?.color}`}>
                      <Icon name="Users" size={14} />
                      <span>{workshop?.registered}/{workshop?.capacity} • {availability?.status}</span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {workshop?.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-1 mb-3">
                    {workshop?.topics?.map((topic, index) => (
                      <span key={index} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                        {topic}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      Instructor: {workshop?.instructorTitle}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => onRegisterWorkshop(workshop)}
                      disabled={workshop?.registered >= workshop?.capacity}
                      iconName="UserPlus"
                      iconPosition="left"
                    >
                      {workshop?.registered >= workshop?.capacity ? 'Full' : 'Register'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <Button variant="outline" fullWidth onClick={onViewAllWorkshops} iconName="Calendar" iconPosition="left">
          View All Workshops
        </Button>
      </div>
    </div>
  );
};

export default UpcomingWorkshops;